import {
  Box,
  Button,
  ButtonProps,
  DatePicker as ChakraDatePicker,
  DatePickerRootProps,
  DateValue,
  Field,
  IconButton,
  Input,
  InputProps,
  Portal,
  Table,
  parseDate,
} from '@chakra-ui/react'
import {LuCalendarDays, LuChevronLeft, LuChevronRight} from 'react-icons/lu'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {useFieldContext} from '../../integrations/form/index.js'
import {Tolgee} from '../../integrations/tolgee/index.js'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'

interface RootProps extends DatePickerRootProps {
  handleDateInput: (e: React.FormEvent<HTMLInputElement>) => void
  handleDateCommit: (e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) => void
  inputProps?: InputProps
  triggerProps?: ButtonProps
}

interface FieldProps extends DatePickerRootProps {
  label: string
  valueAs?: 'date-value' | 'date-string'
  hideLabel?: boolean
  required?: boolean
  disabled?: boolean
  placeholder?: string
  helpText?: string
  maxDate?: DateValue
  minDate?: DateValue
  inputProps?: InputProps
  triggerProps?: ButtonProps
}

type FieldSingleValue = DateValue | string

export function DatePicker({required, hideLabel, children, valueAs, maxDate, minDate, ...props}: FieldProps) {
  const field = useFieldContext<FieldSingleValue | FieldSingleValue[]>()

  const locale = Tolgee.locale
  const separator = getLocaleDateSeparator(locale)

  function format(date: DateValue): string {
    const day = date.day.toString().padStart(2, '0')
    const month = date.month.toString().padStart(2, '0')
    const year = date.year.toString()
    return `${day}${separator}${month}${separator}${year}`
  }

  // auto inserts separators (i.e. '/') as the user types
  function handleDateInput(e: React.FormEvent<HTMLInputElement>) {
    const input = e.currentTarget
    const digits = input.value.replace(/\D/g, '')

    let formatted = digits
    if (digits.length > 2) formatted = `${digits.slice(0, 2)}${separator}${digits.slice(2)}`
    if (digits.length > 4)
      formatted = `${digits.slice(0, 2)}${separator}${digits.slice(2, 4)}${separator}${digits.slice(4, 8)}`

    input.value = formatted
  }

  // on blur or enter, expands a 6 digit input (i.e. '010127') to a full
  // 4 digit year format (i.e. '01/01/2027') visually in the input
  function handleDateCommit(e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) {
    if ('key' in e && e.key !== 'Enter') return

    const input = e.currentTarget
    const digits = input.value.replace(/\D/g, '')

    if (digits.length === 6) {
      const day = digits.slice(0, 2)
      const month = digits.slice(2, 4)
      const year = (2000 + Number.parseInt(digits.slice(4, 6), 10)).toString()
      input.value = `${day}${separator}${month}${separator}${year}`
    }
  }

  const fieldValue = Array.isArray(field.state.value) ? field.state.value : [field.state.value]

  const rootProps: RootProps = {
    fixedWeeks: true,
    positioning: {placement: 'bottom-end', strategy: 'fixed'},
    ...props,
    locale,
    handleDateInput,
    handleDateCommit,
    max: maxDate,
    min: minDate,
    value: (fieldValue
      ?.map(val => {
        if (!val) return undefined
        if (typeof val === 'string' || val instanceof Date) return parseDate(val as string)
        return val
      })
      ?.filter(Boolean) || []) as DateValue[],
    onValueChange: (e: {value: DateValue[]}) => {
      const isSingleMode = 'mode' in field.options && field.options.mode === 'value'
      const newValue = e.value.map(val => {
        if (valueAs === 'date-string') return val.toString()
        return val
      })

      field.handleChange(isSingleMode ? newValue[0] : newValue)
    },
    format,
  }

  if (children) {
    return (
      <FieldRoot required={required} disabled={props.disabled}>
        <DatePickerRoot {...rootProps}>{children}</DatePickerRoot>
      </FieldRoot>
    )
  }

  return (
    <FieldRoot required={required} disabled={props.disabled}>
      <Field.Label srOnly={hideLabel}>
        {props.label}
        {required && <FieldRequiredIndicator />}
      </Field.Label>

      <Field.Context>{() => <DatePickerRoot {...rootProps} />}</Field.Context>

      {props.helpText && <Field.HelperText>{props.helpText}</Field.HelperText>}

      <ErrorMessage />
    </FieldRoot>
  )
}

function DatePickerRoot({
  placeholder,
  handleDateInput,
  handleDateCommit,
  children,
  inputProps,
  triggerProps,
  ...props
}: RootProps) {
  const size = props.size || 'md'

  return (
    <ChakraDatePicker.Root parse={parse} style={{width: '100%'}} {...props}>
      <ChakraDatePicker.Control>
        {!children ? (
          <Box display="flex" width="100%" gap="1">
            <ChakraDatePicker.Input asChild>
              <Input
                size={size}
                placeholder={placeholder}
                bg="bg"
                {...inputProps}
                onInput={handleDateInput}
                onBlur={handleDateCommit}
                onKeyDown={handleDateCommit}
              />
            </ChakraDatePicker.Input>
            <ChakraDatePicker.Trigger asChild>
              <IconButton
                variant="ghost"
                size={size}
                border="solid"
                borderColor="gray.muted"
                borderWidth="1px"
                width="auto"
                height="100%"
                aspectRatio="1"
                bg="bg"
                {...triggerProps}
              >
                <LuCalendarDays />
              </IconButton>
            </ChakraDatePicker.Trigger>
          </Box>
        ) : (
          <ChakraDatePicker.Trigger asChild>
            <Box display="inline-flex" width="auto">
              {children}
            </Box>
          </ChakraDatePicker.Trigger>
        )}
      </ChakraDatePicker.Control>

      <Portal>
        <ChakraDatePicker.Positioner>
          <ChakraDatePicker.Content asChild>
            <Box bg="bg.panel" borderWidth="1px" borderRadius="lg" shadow="lg" zIndex={2000}>
              <ChakraDatePicker.View view="day">
                <ChakraDatePicker.Context>
                  {datePicker => (
                    <>
                      <NavHeader />
                      <ChakraDatePicker.Table asChild>
                        <Table.Root as="div" size="sm">
                          <ChakraDatePicker.TableHead asChild>
                            <Table.Header as="div">
                              <ChakraDatePicker.TableRow asChild>
                                <Table.Row
                                  as="div"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  gap="0"
                                >
                                  {datePicker.weekDays.map(weekDay => (
                                    <ChakraDatePicker.TableHeader key={weekDay.short} asChild>
                                      <Table.ColumnHeader
                                        as="div"
                                        textAlign="center"
                                        fontWeight="medium"
                                        color="fg.muted"
                                        width="100%"
                                      >
                                        {weekDay.short}
                                      </Table.ColumnHeader>
                                    </ChakraDatePicker.TableHeader>
                                  ))}
                                </Table.Row>
                              </ChakraDatePicker.TableRow>
                            </Table.Header>
                          </ChakraDatePicker.TableHead>
                          <ChakraDatePicker.TableBody asChild>
                            <Table.Body as="div">
                              {datePicker.weeks.map((week, weekIndex) => (
                                <ChakraDatePicker.TableRow key={week[0].toString()} asChild>
                                  <Table.Row
                                    as="div"
                                    width="100%"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap="0"
                                  >
                                    {week.map(day => (
                                      <ChakraDatePicker.TableCell
                                        key={day.toString()}
                                        asChild
                                        value={day}
                                        border={weekIndex === datePicker.weeks.length - 1 ? 'none' : undefined}
                                      >
                                        <Table.Cell as="div" textAlign="center" p="0.5" width="100%">
                                          <ChakraDatePicker.TableCellTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              w="full"
                                              h="8"
                                              p="0.5"
                                              fontSize="sm"
                                              _selected={{
                                                bg: 'colorPalette.solid',
                                                color: 'white',
                                              }}
                                              _disabled={{
                                                opacity: 0.4,
                                                cursor: 'not-allowed',
                                              }}
                                            >
                                              {day.day}
                                            </Button>
                                          </ChakraDatePicker.TableCellTrigger>
                                        </Table.Cell>
                                      </ChakraDatePicker.TableCell>
                                    ))}
                                  </Table.Row>
                                </ChakraDatePicker.TableRow>
                              ))}
                            </Table.Body>
                          </ChakraDatePicker.TableBody>
                        </Table.Root>
                      </ChakraDatePicker.Table>
                    </>
                  )}
                </ChakraDatePicker.Context>
              </ChakraDatePicker.View>

              <ChakraDatePicker.View view="month">
                <ChakraDatePicker.Context>
                  {datePicker => (
                    <>
                      <NavHeader />
                      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="2">
                        {datePicker.getMonthsGrid({columns: 4, format: 'short'}).map(months => (
                          <Box key={months[0].value} display="contents">
                            {months.map(month => (
                              <ChakraDatePicker.TableCell key={month.value} value={month.value} asChild>
                                <ChakraDatePicker.TableCellTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    w="full"
                                    _selected={{
                                      bg: 'colorPalette.solid',
                                      color: 'white',
                                    }}
                                  >
                                    {month.label}
                                  </Button>
                                </ChakraDatePicker.TableCellTrigger>
                              </ChakraDatePicker.TableCell>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </ChakraDatePicker.Context>
              </ChakraDatePicker.View>

              <ChakraDatePicker.View view="year">
                <ChakraDatePicker.Context>
                  {datePicker => (
                    <>
                      <NavHeader />
                      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="2">
                        {datePicker.getYearsGrid({columns: 4}).map(years => (
                          <Box key={years[0].value} display="contents">
                            {years.map(year => (
                              <ChakraDatePicker.TableCell key={year.value} value={year.value} asChild>
                                <ChakraDatePicker.TableCellTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    w="full"
                                    _selected={{
                                      bg: 'colorPalette.solid',
                                      color: 'white',
                                    }}
                                  >
                                    {year.label}
                                  </Button>
                                </ChakraDatePicker.TableCellTrigger>
                              </ChakraDatePicker.TableCell>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </ChakraDatePicker.Context>
              </ChakraDatePicker.View>
            </Box>
          </ChakraDatePicker.Content>
        </ChakraDatePicker.Positioner>
      </Portal>
    </ChakraDatePicker.Root>
  )
}

function NavHeader() {
  return (
    <ChakraDatePicker.Context>
      {() => (
        <ChakraDatePicker.ViewControl asChild>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb="4">
            <ChakraDatePicker.PrevTrigger asChild>
              <IconButton variant="ghost" size="sm">
                <LuChevronLeft />
              </IconButton>
            </ChakraDatePicker.PrevTrigger>
            <ChakraDatePicker.ViewTrigger asChild>
              <Button variant="ghost" size="sm" fontWeight="semibold">
                <ChakraDatePicker.RangeText />
              </Button>
            </ChakraDatePicker.ViewTrigger>
            <ChakraDatePicker.NextTrigger asChild>
              <IconButton variant="ghost" size="sm">
                <LuChevronRight />
              </IconButton>
            </ChakraDatePicker.NextTrigger>
          </Box>
        </ChakraDatePicker.ViewControl>
      )}
    </ChakraDatePicker.Context>
  )
}

function getLocaleDateSeparator(locale: string): string {
  // format a known date and extract the separator
  const testDate = new Date(2000, 0, 1)
  const formatted = new Intl.DateTimeFormat(locale).format(testDate)
  const separator = formatted.replace(/\d/g, '')[0]
  return separator || '/'
}

// parses input string to DateValue
// expands 2 digits years to 4 digits
function parse(value: string): DateValue | undefined {
  const digits = value.replace(/\D/g, '')

  const day = digits.slice(0, 2).padStart(2, '0')
  const month = digits.slice(2, 4).padStart(2, '0')

  let year: string
  if (digits.length >= 8) {
    year = digits.slice(4, 8)
  } else if (digits.length === 6) {
    year = (2000 + Number.parseInt(digits.slice(4, 6), 10)).toString()
  } else {
    year = new Date().getFullYear().toString()
  }

  if (!day || day === '00') return undefined

  try {
    return parseDate(`${year}-${month}-${day}`)
  } catch {
    return undefined
  }
}
